// OrgChart.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Panel,
  Background, Controls, MiniMap
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import EmployeeCard from '../../components/lists/EmplyeeList';
import { getAllEmployees } from '../../services/EmployeeService';
import { saveOrgChart, getOrgChart } from '../../services/orgChartAPIs';
import EmployeeNode from './EmployeeNode';
import AnimatedTitle from '../../components/headers/AnimatedHeader';

const nodeTypes = {
  employee: EmployeeNode,
};

// Main OrgChart Component
const OrgChart = () => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingParentId, setPendingParentId] = useState(null);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [employees, setEmployees] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const [edgeMenu, setEdgeMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    edgeId: null
  });


  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Add this inside the OrgChart component, near the top (after hooks)
  const getEdgeCenter = useCallback(({ sourceX, sourceY, targetX, targetY }) => {
    return [(sourceX + targetX) / 2, (sourceY + targetY) / 2];
  }, []);

  // Fetch employees
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const list = await getAllEmployees();
        setEmployees(list);
      } catch (err) {
        console.error('Failed to load employees', err);
      }
    };
    loadEmployees();
  }, []);

  // Load saved chart
  // useEffect(() => {
  //   const loadChart = async () => {
  //     try {
  //       const savedChart = await getOrgChart();
  //       if (savedChart?.nodes?.length > 0) {
  //         const loadedNodes = savedChart.nodes.map(node => ({
  //           id: String(node.id),
  //           type: 'employee',
  //           position: { x: node.positionX, y: node.positionY },
  //           data: { 
  //             employee: node.employee,
  //             isEditMode: false
  //           },
  //         }));

  //         const loadedEdges = (savedChart.edges || []).map(edge => ({
  //           id: String(edge.id),
  //           source: String(edge.sourceNodeId),
  //           target: String(edge.targetNodeId),
  //           type: 'smoothstep',
  //           animated: false,
  //           style: { 
  //             stroke: 'rgba(102, 126, 234, 0.6)', 
  //             strokeWidth: 3
  //           }
  //         }));

  //         setNodes(loadedNodes);
  //         setEdges(loadedEdges);
  //       }
  //     } catch (error) {
  //       console.error('Failed to load org chart:', error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   loadChart();
  // }, []);
  // Load saved chart
  // Load saved chart
  useEffect(() => {
    const loadChart = async () => {
      try {
        const savedChart = await getOrgChart();
        console.log('📊 Loaded chart data:', savedChart);

        if (savedChart?.nodes?.length > 0) {
          // Create a mapping of employeeId to node ID for edge matching
          const employeeToNodeMap = {};

          const loadedNodes = savedChart.nodes.map(node => {
            // Use the employee's employeeId as the node ID to match edges
            const nodeId = String(node.employee?.employeeId || node.id);

            // Store the mapping for edge validation
            employeeToNodeMap[nodeId] = true;

            return {
              id: nodeId,
              type: 'employee',
              position: { x: node.positionX, y: node.positionY },
              data: {
                employee: node.employee,
                isEditMode: false
              },
            };
          });

          console.log('🔍 Employee to Node Map:', employeeToNodeMap);

          // Transform edges to match the actual node IDs
          const loadedEdges = (savedChart.edges || []).map(edge => {
            const sourceId = String(edge.sourceNodeId || edge.source);
            const targetId = String(edge.targetNodeId || edge.target);

            // Check if the edge references exist in our nodes
            const sourceExists = employeeToNodeMap[sourceId];
            const targetExists = employeeToNodeMap[targetId];

            console.log('🔗 Edge validation:', {
              sourceId, sourceExists,
              targetId, targetExists
            });

            return {
              id: edge.id ? String(edge.id) : `e-${sourceId}-${targetId}`,
              source: sourceId,
              target: targetId,
              type: 'smoothstep',
              animated: false,
              style: {
                stroke: 'rgba(102, 126, 234, 0.6)',
                strokeWidth: 3
              },
              // Add hidden flag for invalid edges
              hidden: !sourceExists || !targetExists
            };
          }).filter(edge => !edge.hidden); // Remove invalid edges

          console.log('✅ Final nodes:', loadedNodes);
          console.log('✅ Final edges:', loadedEdges);

          setNodes(loadedNodes);
          setEdges(loadedEdges);

        } else {
          console.log('📭 No saved chart found or empty nodes');
          setNodes([]);
          setEdges([]);
        }
      } catch (error) {
        console.error('❌ Failed to load org chart:', error);
        setNodes([]);
        setEdges([]);
      } finally {
        setLoading(false);
      }
    };
    loadChart();
  }, []);

  // Calculate layout position for new node
  const calculateNodePosition = (parentId, allNodes) => {
    if (!parentId) {
      return { x: 400, y: 100 };
    }

    const parentNode = allNodes.find(n => n.id === String(parentId));
    if (!parentNode) return { x: 400, y: 100 };

    // Find all children of this parent
    const childrenIds = edgesRef.current
      .filter(e => e.source === String(parentId))
      .map(e => e.target);

    const childrenCount = childrenIds.length;
    const horizontalSpacing = 280;
    const verticalSpacing = 200;

    return {
      x: parentNode.position.x + (childrenCount * horizontalSpacing),
      y: parentNode.position.y + verticalSpacing
    };
  };

  const handleAddChild = useCallback((parentId) => {
    console.log('🎯 Add child clicked for parent:', parentId);
    setPendingParentId(String(parentId));
    setShowSelector(true);
  }, []);

  const handleDeleteNode = useCallback((nodeId) => {
    setNodeToDelete(String(nodeId));
  }, []);

  // Sync isEditMode to all nodes
  useEffect(() => {
    setNodes(prevNodes =>
      prevNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          isEditMode,
          onAddChild: handleAddChild,
          onDelete: handleDeleteNode,
        },
      }))
    );
  }, [isEditMode, setNodes, handleAddChild, handleDeleteNode]);

  const buildNode = (employee, position, parentId = null) => {
    if (!employee?.employeeId) {
      console.error('Employee missing employeeId:', employee);
      return null;
    }

    return {
      id: String(employee.employeeId),
      type: 'employee',
      position,
      data: {
        employee,
        isEditMode,
        onAddChild: handleAddChild,
        onDelete: handleDeleteNode,
        parentId: parentId,
      },
    };
  };

  const onNodeChanges = useCallback((changes) => {
    setNodes(nds => applyNodeChanges(changes, nds));
  }, [setNodes]);

  const onEdgeChanges = useCallback((changes) => {
    setEdges(eds => applyEdgeChanges(changes, eds));
  }, [setEdges]);

  const handleConnect = useCallback((connection) => {
    if (isEditMode) {
      return;
    }
    console.log('🔗 Connecting nodes:', connection);
    setEdges(eds => addEdge({
      ...connection,
      type: 'smoothstep',
      animated: false,
      style: {
        stroke: 'rgba(102, 126, 234, 0.6)',
        strokeWidth: 3
      }
    }, eds));
  }, [setEdges]);

  const handleSelectEmployee = useCallback((employeeId, employee) => {
    console.log('✅ Employee selected:', employee, 'Parent:', pendingParentId);

    if (!employee?.employeeId) {
      console.error('Employee missing employeeId:', employee);
      return;
    }

    // Check if employee already exists
    const existingNode = nodesRef.current.find(n => n.id === String(employee.employeeId));
    if (existingNode) {
      alert('This employee is already in the chart!');
      setShowSelector(false);
      setPendingParentId(null);
      return;
    }

    const position = calculateNodePosition(pendingParentId, nodesRef.current);
    const newNode = buildNode(employee, position, pendingParentId);

    if (!newNode) return;

    setNodes(nds => [...nds, newNode]);

    // Create edge if there's a parent
    if (pendingParentId) {
      const newEdge = {
        id: `e-${pendingParentId}-${newNode.id}`,
        source: String(pendingParentId),
        target: String(newNode.id),
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: 'rgba(102, 126, 234, 0.6)',
          strokeWidth: 3
        }
      };
      setEdges(eds => [...eds, newEdge]);
    }

    setShowSelector(false);
    setPendingParentId(null);
  }, [pendingParentId]);

  const handleSaveChart = async () => {
    try {
      const chartData = {
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          positionX: node.position.x,
          positionY: node.position.y,
          employee: node.data.employee,
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          sourceNodeId: edge.source,
          targetNodeId: edge.target,
        })),
      };

      await saveOrgChart(chartData);
      // alert('✅ Organization chart saved successfully!');
      setSuccess(' Organization chart saved successfully!');
    } catch (error) {
      console.error('Failed to save chart:', error);
      // alert('❌ Failed to save chart. Check console for details.');
      setError('Failed to save chart. Check console for details.')
    }
  };

  const confirmDelete = () => {
    if (!nodeToDelete) return;

    // Find all descendants
    const findDescendants = (nodeId) => {
      const children = edgesRef.current
        .filter(e => e.source === nodeId)
        .map(e => e.target);

      let descendants = [...children];
      children.forEach(childId => {
        descendants = [...descendants, ...findDescendants(childId)];
      });

      return descendants;
    };

    const toDelete = [nodeToDelete, ...findDescendants(nodeToDelete)];

    setNodes(nds => nds.filter(n => !toDelete.includes(n.id)));
    setEdges(eds => eds.filter(e => !toDelete.includes(e.source) && !toDelete.includes(e.target)));
    setNodeToDelete(null);
  };

  const handleEdgeClick = (event, edge) => {
    event.preventDefault();
    event.stopPropagation();

    // Option 1: Use click position (simple & reliable)
    const clickX = event.clientX;
    const clickY = event.clientY;

    // Option 2: Use edge center in screen coordinates (more precise)
    const sourceNode = nodesRef.current.find(n => n.id === edge.source);
    const targetNode = nodesRef.current.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode || !reactFlowInstance) {
      // Fallback to click position if anything is missing
      setEdgeMenu({
        visible: true,
        x: clickX,
        y: clickY,
        edgeId: edge.id
      });
      return;
    }

    // Get flow coordinate center
    const [centerX, centerY] = getEdgeCenter({
      sourceX: sourceNode.position.x,
      sourceY: sourceNode.position.y,
      targetX: targetNode.position.x,
      targetY: targetNode.position.y,
    });

    // Convert flow coordinates → screen coordinates
    const screenPos = reactFlowInstance.flowToScreenPosition({ x: centerX, y: centerY });

    setEdgeMenu({
      visible: true,
      x: screenPos.x - 150,
      y: screenPos.y - 120,
      edgeId: edge.id
    });
  };

  const usedEmployeeIds = nodes.map(n => n.data.employee.employeeId);

  if (loading) {
    return (
      <div style={{
        height: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '18px',
        color: '#3d52a0'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          padding: '30px 50px',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          Loading organizational chart...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '90vh',
      width: '100%',
      background: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
      // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <AnimatedTitle
        text="Organizational Chart"
        variant="h1"
        color="#3d52a0"
        delay={0.07}
      />
      <ReactFlow
        ref={reactFlowWrapper}
        onInit={setReactFlowInstance}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodeChanges}
        onEdgesChange={onEdgeChanges}
        nodeTypes={nodeTypes}
        onConnect={handleConnect}
        connectionMode="loose"
        elementsSelectable={isEditMode}
        nodesDraggable={isEditMode}
        nodesConnectable={isEditMode}
        edgesUpdatable={isEditMode}
        fitView
        fitViewOptions={{ padding: 0.2, minZoom: 0.3, maxZoom: 1.5 }}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        onEdgeClick={handleEdgeClick}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
          style: {
            stroke: 'rgba(102, 126, 234, 0.6)',
            strokeWidth: 3
          }
        }}
      >
        <Controls />
        <MiniMap style={{ margin: '0 20px 80px 0' }} />
        <Background
          color="rgba(255, 255, 255, 0.2)"
          gap={20}
          size={1}
        />

        {/* Control Panel - Glassmorphism Style */}
        <Panel position="bottom-left" style={{ margin: '0 0 0 60px' }}>
          <div style={{
            // background: 'rgba(255, 255, 255, 0.15)',
            // backdropFilter: 'blur(20px)',
            // WebkitBackdropFilter: 'blur(20px)',
            padding: '16px',
            // borderRadius: '16px',
            // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            // border: '1px solid rgba(255, 255, 255, 0.3)',
            display: 'flex',
            gap: '10px',
            flexDirection: 'row'
          }}>
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              style={{
                padding: '12px 18px',
                background: isEditMode
                  ? 'rgba(245, 158, 11, 0.9)'
                  : 'rgba(102, 126, 234, 0.9)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
              }}
            >
              {isEditMode ? '✅ Exit Edit Mode' : '✏️ Edit Mode'}
            </button>

            <button
              onClick={handleSaveChart}
              style={{
                padding: '12px 18px',
                background: 'rgba(16, 185, 129, 0.9)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
              }}
            >
              💾 Save Chart
            </button>

            {nodes.length === 0 && (
              <button
                onClick={() => {
                  setPendingParentId(null);
                  setShowSelector(true);
                }}
                style={{
                  padding: '12px 18px',
                  background: 'rgba(59, 130, 246, 0.9)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                }}
              >
                ➕ Add First Employee
              </button>
            )}
            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            )}
          </div>
        </Panel>

        {/* Info Panel */}
        <Panel position="bottom-right">
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            padding: '10px 16px',
            borderRadius: '12px',
            fontSize: '13px',
            color: 'blue',
            fontWeight: '600',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            👥 {nodes.length} employees • 🔗 {edges.length} connections
          </div>
        </Panel>

        {edgeMenu.visible && (
          <div
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              top: edgeMenu.y,
              left: edgeMenu.x,
              background: 'var(--card-bg, white)',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              padding: '8px 0',
              minWidth: '140px'
            }}
          >
            <button
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer'
              }}
              onClick={() => {
                setEdges(eds => eds.filter(e => e.id !== edgeMenu.edgeId));
                setEdgeMenu({ visible: false, x: 0, y: 0, edgeId: null });
              }}
            >
              🗑️ Delete Edge
            </button>

            <button
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer'
              }}
              onClick={() => {
                setEdges(eds =>
                  eds.map(e =>
                    e.id === edgeMenu.edgeId
                      ? {
                        ...e,
                        type:
                          e.type === 'smoothstep'
                            ? 'bezier'
                            : e.type === 'bezier'
                              ? 'straight'
                              : 'smoothstep'
                      }
                      : e
                  )
                );
                setEdgeMenu({ visible: false, x: 0, y: 0, edgeId: null });
              }}
            >
              🔄 Change Edge Type
            </button>
          </div>
        )}

      </ReactFlow>

      {/* Employee Selector Modal - Glassmorphism */}
      <Dialog
        open={showSelector}
        onClose={() => {
          setShowSelector(false);
          setPendingParentId(null);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(30px)',
            borderRadius: '16px',
            border: '1px solid rgba(0, 115, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle style={{ fontWeight: '700', color: '#1f2937' }}>
          Select an Employee
        </DialogTitle>
        <DialogContent>
          <EmployeeCard
            employees={employees.filter(emp => !usedEmployeeIds.includes(emp.employeeId))}
            onSelectEmployee={handleSelectEmployee}
            itemsPerPage={8}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSelector(false);
              setPendingParentId(null);
            }}
            style={{
              background: 'rgba(107, 114, 128, 0.9)',
              color: 'white',
              fontWeight: '600'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Glassmorphism */}
      <Dialog
        open={!!nodeToDelete}
        onClose={() => setNodeToDelete(null)}
        PaperProps={{
          style: {
            background: 'var(--card-bg, rgba(255, 255, 255, 0.95))',
            backdropFilter: 'blur(30px)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle style={{ fontWeight: '700', color: '#1f2937' }}>
          ⚠️ Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <div style={{ color: '#4b5563' }}>
            Are you sure you want to delete this employee and all their direct reports?
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNodeToDelete(null)}
            style={{
              background: 'rgba(229, 231, 235, 0.9)',
              color: '#1f2937',
              fontWeight: '600'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            style={{
              background: 'rgba(239, 68, 68, 0.9)',
              color: 'white',
              fontWeight: '600'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default function OrgChartWithProvider() {
  return (
    <ReactFlowProvider>
      <OrgChart />
    </ReactFlowProvider>
  );
}